import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormLabel, FormMessage } from '@/components/ui/form';
import { filesApi } from '@/core/api/files.api';
import { FILE_FOLDERS } from '@/constants/file-types';
import { toast } from 'react-toastify';
import { eliminaFotografia } from './photo-upload-field.utils';

interface PhotoUploadFieldProps {
  value?: string[];
  onChange: (urls: string[]) => void;
  error?: string;
  disabled?: boolean;
  maxPhotos?: number;
  label?: string;
}

/**
 * PhotoUploadField - Reusable photo upload component
 *
 * Features:
 * - Drag & drop or click to upload
 * - Multiple file support (2-10 photos)
 * - Automatic upload to Azure Blob Storage
 * - Thumbnail previews with delete buttons
 * - Integration with react-hook-form via Controller
 * - Memory-safe (no object URL leaks, files uploaded immediately)
 * - Design system utilities from main.css (no inline styles)
 *
 * Styling:
 * - Uses @utility classes from main.css
 * - Follows Ecotic design system patterns
 * - Consistent with form-field-row and form-label-bold utilities
 *
 * @example
 * <Controller
 *   control={form.control}
 *   name="photos"
 *   render={({ field, fieldState }) => (
 *     <PhotoUploadField
 *       value={field.value}
 *       onChange={field.onChange}
 *       error={fieldState.error?.message}
 *       disabled={loading}
 *       maxPhotos={10}
 *     />
 *   )}
 * />
 */
export function PhotoUploadField({
  value = [],
  onChange,
  error,
  disabled = false,
  maxPhotos = 10,
  label = 'Fotografii:',
}: PhotoUploadFieldProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [imageLoadStates, setImageLoadStates] = useState<Record<number, 'loading' | 'loaded' | 'error'>>({});

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      setIsUploading(true);
      try {
        // Upload all files in parallel
        const uploadPromises = acceptedFiles.map((file) =>
          filesApi.uploadFile(file, FILE_FOLDERS.WASTE_COLLECTION_FORM_PHOTOS)
        );

        // filesApi.uploadFile now returns string URLs directly
        const uploadedUrls = await Promise.all(uploadPromises);

        // Append new URLs to existing photos
        onChange([...value, ...uploadedUrls]);
      } catch (error) {
        console.error('Failed to upload photos:', error);
        // Toast notifications are handled by filesApi.uploadFile
      } finally {
        setIsUploading(false);
      }
    },
    [value, onChange]
  );

  const remainingSlots = maxPhotos - value.length;
  const canUpload = !disabled && !isUploading && remainingSlots > 0;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxFiles: remainingSlots,
    disabled: !canUpload,
    multiple: true,
  });

  const removePhoto = async (index: number) => {
    if (disabled || isUploading || deletingIndex !== null) return;

    setDeletingIndex(index);
    try {
      // Poza se scoate din formular INDIFERENT de rezultatul stergerii din storage: un
      // PROFESOR primeste 403 la DELETE (doar DBEST/ADMIN au voie), dar tot vrea sa scoata
      // poza din formular -- vezi eliminaFotografia().
      const { urls, stergereaDinStorageAEsuat } = await eliminaFotografia(
        (url) => filesApi.deleteFile(url),
        value,
        index
      );
      onChange(urls);
      if (!stergereaDinStorageAEsuat) {
        toast.success('Fotografia a fost ștearsă cu succes');
      }
    } finally {
      setDeletingIndex(null);
    }
  };

  const handleImageLoad = (index: number) => {
    setImageLoadStates(prev => ({ ...prev, [index]: 'loaded' }));
  };

  const handleImageError = (index: number) => {
    setImageLoadStates(prev => ({ ...prev, [index]: 'error' }));
    console.error(`Failed to load image at index ${index}:`, value[index]);
  };

  return (
    <div className="form-field-row">
      <FormLabel className="form-label-bold">{label}</FormLabel>
      <div className="flex-1 space-y-2">
        {/* Dropzone Area */}
        <div
          {...getRootProps()}
          className={`
            photo-upload-dropzone bg-white
            ${isDragActive ? 'photo-upload-dropzone-active' : ''}
            ${!canUpload ? 'photo-upload-dropzone-disabled' : ''}
          `}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-2">
            {/* Pictograma zonei de incarcare: `text-gray-400` da 2,54:1, sub pragul
                de 3:1 pentru elemente grafice. `text-muted-foreground` da 4,83.
                Are text alaturi, deci nu poarta singura informatia — dar tot se
                vede prost pe un ecran in soare. */}
            <svg
              aria-hidden
              className="w-12 h-12 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <div className="text-sm text-gray-600">
              {isUploading ? (
                <span className="font-medium text-primary">Se încarcă fotografiile...</span>
              ) : isDragActive ? (
                <span className="font-medium text-primary">Eliberați fișierele aici...</span>
              ) : (
                <>
                  <span className="font-medium">Trageți fotografiile aici</span> sau{' '}
                  <span className="text-primary underline">click pentru a selecta</span>
                </>
              )}
            </div>
            <p className="text-small text-gray-500">
              {value.length}/{maxPhotos} fotografii • PNG, JPG, JPEG, WEBP
            </p>
          </div>
        </div>

        {/* Photo Grid with Thumbnails */}
        {value.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
            {value.map((url, index) => {
              const isDeleting = deletingIndex === index;
              const loadState = imageLoadStates[index] || 'loading';

              return (
                <div key={url} className="photo-upload-grid-item">
                  <div className="photo-upload-image-container">
                    {/* Image */}
                    <img
                      src={url}
                      alt={`Fotografie ${index + 1}`}
                      className={`photo-upload-image ${
                        loadState === 'loaded' ? 'opacity-100' : 'opacity-0'
                      }`}
                      onLoad={() => handleImageLoad(index)}
                      onError={() => handleImageError(index)}
                    />

                    {/* Loading Spinner */}
                    {loadState === 'loading' && (
                      <div className="photo-upload-spinner">
                        <div />
                      </div>
                    )}

                    {/* Error State */}
                    {loadState === 'error' && (
                      <div className="photo-upload-error-state">
                        <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <span className="text-small">Eroare la încărcare</span>
                      </div>
                    )}

                    {/* Deleting Overlay */}
                    {isDeleting && (
                      <div className="photo-upload-delete-overlay">
                        <div />
                      </div>
                    )}
                  </div>

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    disabled={disabled || isUploading || isDeleting}
                    className="photo-upload-delete-btn"
                    aria-label={`Șterge fotografia ${index + 1}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>

                  {/* Photo Number Badge */}
                  <div className="photo-upload-badge">
                    {index + 1}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Error Message */}
        {error && <FormMessage className="form-message-spacer">{error}</FormMessage>}

        {/* Helper Text */}
        {!error && value.length === 0 && (
          <p className="text-small text-gray-500 mt-1">Minim 2 fotografii sunt necesare</p>
        )}
      </div>
    </div>
  );
}
